import React, { useEffect, useState } from "react"
import { Button, Card, CardBody, CardTitle, Col, Row } from "reactstrap"
import { Form, Field, Formik } from "lib/formik"
import Breadcrumbs from "components/Common/Breadcrumb"
import BackButton from "components/Shared/BackButton"
import { useLocation, useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import { showAlert } from "store/reducers/alertReducer"
import Spinners from "components/Shared/Spinners"
import { addGiftCardTemplateSchema } from "components/Common/validation"
import { getAllGiftCardCategories } from "store/actions/giftCardCategoriesAction"
import isPermissionEnabled from "common/isPermissionEnabled"
import { useTranslation } from "react-i18next"
import TextError from "components/Shared/TextError"
import TranslatedFileInput from "components/Shared/TranslatedFileInput"
import { giftCardSize } from "components/Common/ImageSize"
import {
  getImagePreviewSrc2,
  handleFileChange3,
} from "components/Shared/fileHandlers"
import {
  addTemplate,
  getTemplateById,
  updateTemplate,
} from "store/actions/templatesAction"
import Select from "components/Shared/TranslatedSelect"

const initialValues = {
  cardTypeId: "",
  card_title_on_app_en: "",
  card_title_on_app_ar: "",
  share_gift_card_title_en: "",
  share_gift_card_title_ar: "",
  share_gift_card_desc_en: "",
  share_gift_card_desc_ar: "",
  purchased_gift_card_popup_title_en: "",
  purchased_gift_card_popup_title_ar: "",
  purchased_gift_card_popup_desc_en: "",
  purchased_gift_card_popup_desc_ar: "",
  redeem_gift_card_message_en: "",
  redeem_gift_card_message_ar: "",
  gift_card_received_appnotify_title_en: "",
  gift_card_received_appnotify_title_ar: "",
  gift_card_received_appnotify_desc_en: "",
  gift_card_received_appnotify_desc_ar: "",
  sms_message_for_receiver_en: "",
  sms_message_for_receiver_ar: "",
  socialmedia_message_for_receiver_en: "",
  socialmedia_message_for_receiver_ar: "",
  confirm_redeem_card_title_en: "",
  confirm_redeem_card_title_ar: "",
  confirm_redeem_card_message_en: "",
  confirm_redeem_card_message_ar: "",
  redeem_now_button_en: "",
  redeem_now_button_ar: "",
  redeem_gift_card_title_ar: "",
  redeem_gift_card_title_en: "",
  image: "",
  whatsapp_message_template: "",
  card_rcvd_title_en: "",
  card_rcvd_title_ar: "",
  card_rcvd_msg_en: "",
  card_rcvd_msg_ar: "",
  add_to_wallet_app_notify_title_en: "",
  add_to_wallet_app_notify_title_ar: "",
  add_to_wallet_app_notify_msg_en: "",
  add_to_wallet_app_notify_msg_ar: "",
}

const AddTemplate = ({ mode }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = `${mode === "add" ? t("Add") : t("Edit")} ${t(
    "Gift Card Template "
  )}`

  const location = useLocation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const cardType = searchParams.get("cardType")

  const { loading, isLoading, template } = useSelector(state => state.Templates)
  const { categories } = useSelector(state => state.GiftCardCategories)
  const { userRole, accountType } = useSelector(state => state.Login)

  const [formValues, setFormValues] = useState(initialValues)
  const [selectedCategory, setSelectedCategory] = useState(null)

  useEffect(() => {
    // Fetch categories when component mounts
    dispatch(
      getAllGiftCardCategories({
        perPage: 1000, // Get all categories
        pageNo: 1,
        active: null, // active/inactive both categories
        orderBy: 1,
        keyword: "",
        isDeleted: false, //only get types which are not deleted
      })
    )
  }, [])

  const handleCategorySelect = (selectedOption, setFieldValue) => {
    setSelectedCategory(selectedOption)
    setFieldValue("cardTypeId", selectedOption?.value || "")
  }

  useEffect(() => {
    const isPermissionCreate = isPermissionEnabled(
      userRole,
      37,
      "gift_card_2",
      accountType
    )
    const isPermissionEdit = isPermissionEnabled(
      userRole,
      37,
      "gift_card_3",
      accountType
    )

    if (mode === "add") {
      if (!isPermissionCreate) {
        navigate("/dashboard")
      }
    } else if (mode === "edit") {
      if (!isPermissionEdit) {
        navigate("/dashboard")
      }
    }
  }, [])

  useEffect(() => {
    if (mode === "edit") {
      getOldValues()
    }
  }, [])

  const getOldValues = () => {
    dispatch(getTemplateById({ cardTypeId: cardType }))
  }

  useEffect(() => {
    if (template && !loading && mode === "edit") {
      const updatedFormValues = {
        ...initialValues,
        ...template,
        cardTypeId: template?.cardTypeId?._id,
        image: template?.popup_image,
      }

      setFormValues(updatedFormValues)

      // Set the selected category for the Select component
      if (template.cardTypeId && categories?.length > 0) {
        const categoryOption = categories.find(
          cat => cat._id === template.cardTypeId._id
        )
        if (categoryOption) {
          setSelectedCategory({
            value: categoryOption._id,
            label: categoryOption.cardType,
          })
        }
      }
    }
  }, [template, categories, loading, mode])

  const handleOnSubmit = values => {
    if (mode === "add") {
      const addPayload = {
        cardTypeId: values.cardTypeId,
        card_title_on_app_en: values.card_title_on_app_en,
        card_title_on_app_ar: values.card_title_on_app_ar,
        share_gift_card_title_en: values.share_gift_card_title_en,
        share_gift_card_title_ar: values.share_gift_card_title_ar,
        share_gift_card_desc_en: values.share_gift_card_desc_en,
        share_gift_card_desc_ar: values.share_gift_card_desc_ar,
        purchased_gift_card_popup_title_en:
          values.purchased_gift_card_popup_title_en,
        purchased_gift_card_popup_title_ar:
          values.purchased_gift_card_popup_title_ar,
        purchased_gift_card_popup_desc_en:
          values.purchased_gift_card_popup_desc_en,
        purchased_gift_card_popup_desc_ar:
          values.purchased_gift_card_popup_desc_ar,
        redeem_gift_card_message_en: values.redeem_gift_card_message_en,
        redeem_gift_card_message_ar: values.redeem_gift_card_message_ar,
        gift_card_received_appnotify_title_en:
          values.gift_card_received_appnotify_title_en,
        gift_card_received_appnotify_title_ar:
          values.gift_card_received_appnotify_title_ar,
        gift_card_received_appnotify_desc_en:
          values.gift_card_received_appnotify_desc_en,
        gift_card_received_appnotify_desc_ar:
          values.gift_card_received_appnotify_desc_ar,
        sms_message_for_receiver_en: values.sms_message_for_receiver_en,
        sms_message_for_receiver_ar: values.sms_message_for_receiver_ar,
        socialmedia_message_for_receiver_en:
          values.socialmedia_message_for_receiver_en,
        socialmedia_message_for_receiver_ar:
          values.socialmedia_message_for_receiver_ar,
        confirm_redeem_card_title_en: values.confirm_redeem_card_title_en,
        confirm_redeem_card_title_ar: values.confirm_redeem_card_title_ar,
        confirm_redeem_card_message_en: values.confirm_redeem_card_message_en,
        confirm_redeem_card_message_ar: values.confirm_redeem_card_message_ar,
        redeem_now_button_en: values.redeem_now_button_en,
        redeem_now_button_ar: values.redeem_now_button_ar,
        redeem_gift_card_title_ar: values.redeem_gift_card_title_ar,
        redeem_gift_card_title_en: values.redeem_gift_card_title_en,
        whatsapp_message_template: values.whatsapp_message_template,
        card_rcvd_title_en: values.card_rcvd_title_en,
        card_rcvd_title_ar: values.card_rcvd_title_ar,
        card_rcvd_msg_en: values.card_rcvd_msg_en,
        card_rcvd_msg_ar: values.card_rcvd_msg_ar,
        add_to_wallet_app_notify_title_en:
          values.add_to_wallet_app_notify_title_en,
        add_to_wallet_app_notify_title_ar:
          values.add_to_wallet_app_notify_title_ar,
        add_to_wallet_app_notify_msg_en: values.add_to_wallet_app_notify_msg_en,
        add_to_wallet_app_notify_msg_ar: values.add_to_wallet_app_notify_msg_ar,
      }

      const formData = new FormData()

      Object.entries(addPayload).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, value)
        }
      })

      formData.append("popup_image", values?.image)

      dispatch(addTemplate(formData)).then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status == true) {
          dispatch(showAlert({ text: msg, type: "success" }))
          navigate(-1)
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
    } else {
      const updatePayload = {
        id: template._id,
        cardTypeId: values.cardTypeId,
        card_title_on_app_en: values.card_title_on_app_en,
        card_title_on_app_ar: values.card_title_on_app_ar,
        share_gift_card_title_en: values.share_gift_card_title_en,
        share_gift_card_title_ar: values.share_gift_card_title_ar,
        share_gift_card_desc_en: values.share_gift_card_desc_en,
        share_gift_card_desc_ar: values.share_gift_card_desc_ar,
        purchased_gift_card_popup_title_en:
          values.purchased_gift_card_popup_title_en,
        purchased_gift_card_popup_title_ar:
          values.purchased_gift_card_popup_title_ar,
        purchased_gift_card_popup_desc_en:
          values.purchased_gift_card_popup_desc_en,
        purchased_gift_card_popup_desc_ar:
          values.purchased_gift_card_popup_desc_ar,
        redeem_gift_card_message_en: values.redeem_gift_card_message_en,
        redeem_gift_card_message_ar: values.redeem_gift_card_message_ar,
        gift_card_received_appnotify_title_en:
          values.gift_card_received_appnotify_title_en,
        gift_card_received_appnotify_title_ar:
          values.gift_card_received_appnotify_title_ar,
        gift_card_received_appnotify_desc_en:
          values.gift_card_received_appnotify_desc_en,
        gift_card_received_appnotify_desc_ar:
          values.gift_card_received_appnotify_desc_ar,
        sms_message_for_receiver_en: values.sms_message_for_receiver_en,
        sms_message_for_receiver_ar: values.sms_message_for_receiver_ar,
        socialmedia_message_for_receiver_en:
          values.socialmedia_message_for_receiver_en,
        socialmedia_message_for_receiver_ar:
          values.socialmedia_message_for_receiver_ar,
        confirm_redeem_card_title_en: values.confirm_redeem_card_title_en,
        confirm_redeem_card_title_ar: values.confirm_redeem_card_title_ar,
        confirm_redeem_card_message_en: values.confirm_redeem_card_message_en,
        confirm_redeem_card_message_ar: values.confirm_redeem_card_message_ar,
        redeem_now_button_en: values.redeem_now_button_en,
        redeem_now_button_ar: values.redeem_now_button_ar,
        redeem_gift_card_title_ar: values.redeem_gift_card_title_ar,
        redeem_gift_card_title_en: values.redeem_gift_card_title_en,
        whatsapp_message_template: values.whatsapp_message_template,
        card_rcvd_title_en: values.card_rcvd_title_en,
        card_rcvd_title_ar: values.card_rcvd_title_ar,
        card_rcvd_msg_en: values.card_rcvd_msg_en,
        card_rcvd_msg_ar: values.card_rcvd_msg_ar,
        add_to_wallet_app_notify_title_en:
          values.add_to_wallet_app_notify_title_en,
        add_to_wallet_app_notify_title_ar:
          values.add_to_wallet_app_notify_title_ar,
        add_to_wallet_app_notify_msg_en: values.add_to_wallet_app_notify_msg_en,
        add_to_wallet_app_notify_msg_ar: values.add_to_wallet_app_notify_msg_ar,
      }

      const formData = new FormData()

      Object.entries(updatePayload).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, value)
        }
      })
      formData.append("popup_image", values?.image)
      dispatch(updateTemplate(formData)).then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(
            showAlert({
              text: "Template updated successfully",
              type: "success",
            })
          )
          navigate(-1)
        }
      })
    }
  }

  if (mode === "edit" && loading) return <Spinners />

  const categoryOptions =
    categories?.map(category => ({
      value: category._id,
      label: category.cardType,
    })) || []

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <CardBody>
                <CardTitle className="mb-2 d-flex align-items-center">
                  <BackButton />
                  {mode === "add" ? t("Add") : t("Edit")}{" "}
                  {t("Gift Card Template")}
                </CardTitle>
                <Formik
                  initialValues={formValues}
                  enableReinitialize={true}
                  validationSchema={addGiftCardTemplateSchema()}
                  onSubmit={handleOnSubmit}
                >
                  {({ errors, touched, setFieldValue, values }) => (
                    <Form>
                      <h5 className="mt-5">Sender Screen</h5>
                      <hr style={{ border: "1px solid #dee2e6", opacity: 1 }} />
                      <div className="row">
                        <div className="col-12">
                          <div className="form_field">
                            <label htmlFor="cardTypeId">
                              <span className="text-danger">*</span>{" "}
                              {t("Card Type")}
                            </label>
                            <Select
                              value={selectedCategory}
                              onChange={option =>
                                handleCategorySelect(option, setFieldValue)
                              }
                              isDisabled={mode === "edit"}
                              options={categoryOptions}
                              placeholder={t("Select Card Type")}
                              className="select2-selection"
                              classNamePrefix="select2"
                              isClearable
                              isSearchable
                              styles={{
                                control: base => ({
                                  ...base,
                                  minHeight: "38px",
                                  height: "38px",
                                }),
                                valueContainer: base => ({
                                  ...base,
                                  height: "38px",
                                  padding: "0 8px",
                                }),
                                input: base => ({
                                  ...base,
                                  margin: "0px",
                                }),
                                indicatorsContainer: base => ({
                                  ...base,
                                  height: "38px",
                                }),
                              }}
                            />
                            <Field type="hidden" name="cardTypeId" />
                            <TextError name="cardTypeId" />
                          </div>
                        </div>

                        {/* Card Title Section */}
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="card_title_on_app_en">
                              <span className="text-danger">*</span>{" "}
                              {t("Card Title (English)")}
                            </label>
                            <Field
                              type="text"
                              id="card_title_on_app_en"
                              name="card_title_on_app_en"
                              placeholder={t("Enter Card Title (English)")}
                              className={`form-control ${
                                touched.card_title_on_app_en &&
                                errors.card_title_on_app_en
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="card_title_on_app_en" />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="card_title_on_app_ar">
                              <span className="text-danger">*</span>{" "}
                              {t("Card Title (عربي)")}
                            </label>
                            <Field
                              type="text"
                              style={{ direction: "rtl" }}
                              id="card_title_on_app_ar"
                              name="card_title_on_app_ar"
                              placeholder={t("Enter Card Title (عربي)")}
                              className={`form-control ${
                                touched.card_title_on_app_ar &&
                                errors.card_title_on_app_ar
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="card_title_on_app_ar" />
                          </div>
                        </div>

                        {/* Share Gift Card Section */}
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="share_gift_card_title_en">
                              {t("Share Gift Card Title (English)")}
                            </label>
                            <Field
                              type="text"
                              id="share_gift_card_title_en"
                              name="share_gift_card_title_en"
                              placeholder={t(
                                "Enter Share Gift Card Title (English)"
                              )}
                              className="form-control"
                            />
                            <TextError name="share_gift_card_title_en" />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="share_gift_card_title_ar">
                              {t("Share Gift Card Title (عربي)")}
                            </label>
                            <Field
                              type="text"
                              style={{ direction: "rtl" }}
                              id="share_gift_card_title_ar"
                              name="share_gift_card_title_ar"
                              placeholder={t(
                                "Enter Share Gift Card Title (عربي)"
                              )}
                              className="form-control"
                            />
                            <TextError name="share_gift_card_title_ar" />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="share_gift_card_desc_en">
                              {t("Share Gift Card Description (English)")}
                            </label>
                            <Field
                              as="textarea"
                              rows={3}
                              id="share_gift_card_desc_en"
                              name="share_gift_card_desc_en"
                              placeholder={t(
                                "Enter Share Gift Card Description (English)"
                              )}
                              className="form-control"
                            />
                            <TextError name="share_gift_card_desc_en" />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="share_gift_card_desc_ar">
                              {t("Share Gift Card Description (عربي)")}
                            </label>
                            <Field
                              as="textarea"
                              rows={3}
                              style={{ direction: "rtl" }}
                              id="share_gift_card_desc_ar"
                              name="share_gift_card_desc_ar"
                              placeholder={t(
                                "Enter Share Gift Card Description (عربي)"
                              )}
                              className="form-control"
                            />
                            <TextError name="share_gift_card_desc_ar" />
                          </div>
                        </div>
                      </div>

                      {/* Purchased Gift Card Popup Section */}

                      <h5 className="mt-5">Payment Screen</h5>
                      <hr style={{ border: "1px solid #dee2e6", opacity: 1 }} />
                      <div className="row">
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="purchased_gift_card_popup_title_en">
                              {t("Purchase Popup Title (English)")}
                            </label>
                            <Field
                              type="text"
                              id="purchased_gift_card_popup_title_en"
                              name="purchased_gift_card_popup_title_en"
                              placeholder={t(
                                "Enter Purchase Popup Title (English)"
                              )}
                              className="form-control"
                            />
                            <TextError name="purchased_gift_card_popup_title_en" />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="purchased_gift_card_popup_title_ar">
                              {t("Purchase Popup Title (عربي)")}
                            </label>
                            <Field
                              type="text"
                              style={{ direction: "rtl" }}
                              id="purchased_gift_card_popup_title_ar"
                              name="purchased_gift_card_popup_title_ar"
                              placeholder={t(
                                "Enter Purchase Popup Title (عربي)"
                              )}
                              className="form-control"
                            />
                            <TextError name="purchased_gift_card_popup_title_ar" />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="purchased_gift_card_popup_desc_en">
                              {t("Purchase Popup Description (English)")}
                            </label>
                            <Field
                              as="textarea"
                              rows={3}
                              id="purchased_gift_card_popup_desc_en"
                              name="purchased_gift_card_popup_desc_en"
                              placeholder={t(
                                "Enter Purchase Popup Description (English)"
                              )}
                              className="form-control"
                            />
                            <TextError name="purchased_gift_card_popup_desc_en" />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="purchased_gift_card_popup_desc_ar">
                              {t("Purchase Popup Description (عربي)")}
                            </label>
                            <Field
                              as="textarea"
                              rows={3}
                              style={{ direction: "rtl" }}
                              id="purchased_gift_card_popup_desc_ar"
                              name="purchased_gift_card_popup_desc_ar"
                              placeholder={t(
                                "Enter Purchase Popup Description (عربي)"
                              )}
                              className="form-control"
                            />
                            <TextError name="purchased_gift_card_popup_desc_ar" />
                          </div>
                        </div>
                      </div>

                      {/* Redeem Gift Card Section */}
                      <h5 className="mt-5">Receiver Notification</h5>
                      <hr style={{ border: "1px solid #dee2e6", opacity: 1 }} />

                      {/*<h5 className="mt-2">First Dialog Box</h5>*/}
                      <div className="row">
                        {/* Confirm Redeem Card Section */}
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="confirm_redeem_card_title_en">
                              {t("First Dialog Box Title (English)")}
                            </label>
                            <Field
                              type="text"
                              id="confirm_redeem_card_title_en"
                              name="confirm_redeem_card_title_en"
                              placeholder={t(
                                "Enter First Dialog Box Title (English)"
                              )}
                              className="form-control"
                            />
                            <TextError name="confirm_redeem_card_title_en" />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="confirm_redeem_card_title_ar">
                              {t("First Dialog Box Title (عربي)")}
                            </label>
                            <Field
                              type="text"
                              style={{ direction: "rtl" }}
                              id="confirm_redeem_card_title_ar"
                              name="confirm_redeem_card_title_ar"
                              placeholder={t(
                                "Enter First Dialog Box Title (عربي)"
                              )}
                              className="form-control"
                            />
                            <TextError name="confirm_redeem_card_title_ar" />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="confirm_redeem_card_message_en">
                              {t("First Dialog Box Message Body (English)")}
                            </label>
                            <Field
                              type="text"
                              id="confirm_redeem_card_message_en"
                              name="confirm_redeem_card_message_en"
                              placeholder={t(
                                "Enter First Dialog Body (English)"
                              )}
                              className="form-control"
                            />
                            <TextError name="confirm_redeem_card_message_en" />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="confirm_redeem_card_message_ar">
                              {t("First Dialog Box Body (عربي)")}
                            </label>
                            <Field
                              type="text"
                              style={{ direction: "rtl" }}
                              id="confirm_redeem_card_message_ar"
                              name="confirm_redeem_card_message_ar"
                              placeholder={t(
                                "Enter First Dialog Box Message Body (عربي)"
                              )}
                              className="form-control"
                            />
                            <TextError name="confirm_redeem_card_message_ar" />
                          </div>
                        </div>

                        <div className="col-lg-12">
                          <div className="form_field position-relative">
                            <label htmlFor="image">
                              <span className="text-danger">*</span>{" "}
                              {t("Upload Dialog Box Image")}
                            </label>
                            <TranslatedFileInput
                              id="image"
                              name="image"
                              accept="image/jpeg, image/jpg, image/png, image/webp"
                              invalid={!!(touched.image && errors.image)}
                              onChange={e => {
                                handleFileChange3(
                                  e,
                                  "image",
                                  setFieldValue,
                                  giftCardSize
                                )
                              }}
                            />
                            <TextError
                              name="image"
                              component="div"
                              className="text-danger"
                            />
                          </div>
                          <span>
                            {t(
                              "Recommended dimensions: Size should be less than or equal to {{size}} KB",
                              {
                                size: giftCardSize,
                              }
                            )}
                          </span>
                          {getImagePreviewSrc2(values.image) && (
                            <div className="mt-3">
                              <img
                                src={getImagePreviewSrc2(values.image)}
                                alt="Preview"
                                className="img_preview"
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Received Fields */}

                      <div className="row">
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="card_rcvd_title_en">
                              {t("Gift Received Title (English)")}
                            </label>
                            <Field
                              type="text"
                              id="card_rcvd_title_en"
                              name="card_rcvd_title_en"
                              placeholder={t("Enter Gift Received Title")}
                              className="form-control"
                            />
                            <TextError name="card_rcvd_title_en" />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="card_rcvd_title_ar">
                              {t("Gift Received Title  (عربي)")}
                            </label>
                            <Field
                              type="text"
                              id="card_rcvd_title_ar"
                              name="card_rcvd_title_ar"
                              placeholder={t("Enter Gift Received Title")}
                              className="form-control"
                            />
                            <TextError name="card_rcvd_title_ar" />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="card_rcvd_msg_en">
                              {t("Gift Received Message (English)")}
                            </label>
                            <Field
                              type="text"
                              id="card_rcvd_msg_en"
                              name="card_rcvd_msg_en"
                              placeholder={t("Enter Gift Received Message")}
                              className="form-control"
                            />
                            <TextError name="card_rcvd_msg_en" />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="card_rcvd_msg_ar">
                              {t("Gift Received Message  (عربي)")}
                            </label>
                            <Field
                              type="text"
                              id="card_rcvd_msg_ar"
                              name="card_rcvd_msg_ar"
                              placeholder={t("Enter Gift Received Message")}
                              className="form-control"
                            />
                            <TextError name="card_rcvd_msg_ar" />
                          </div>
                        </div>
                      </div>

                      {/*<h5 className="mt-5">Second Dialog Box</h5>*/}
                      <div className="row">
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="confirm_redeem_card_message_en">
                              {t("Second Dialog Box Title (English)")}
                            </label>
                            <Field
                              type="text"
                              id="redeem_gift_card_title_en"
                              name="redeem_gift_card_title_en"
                              placeholder={t(
                                "Enter Second Dialog Box Title (English)"
                              )}
                              className="form-control"
                            />
                            <TextError name="redeem_gift_card_title_en" />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="confirm_redeem_card_message_ar">
                              {t("Second Dialog Box Title (عربي)")}
                            </label>
                            <Field
                              type="text"
                              style={{ direction: "rtl" }}
                              id="redeem_gift_card_title_ar"
                              name="redeem_gift_card_title_ar"
                              placeholder={t(
                                "Enter Second Dialog Box Title (عربي)"
                              )}
                              className="form-control"
                            />
                            <TextError name="redeem_gift_card_title_ar" />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="redeem_gift_card_message_en">
                              {t("Second Dialog Box Message Body (English)")}
                            </label>
                            <Field
                              type="text"
                              id="redeem_gift_card_message_en"
                              name="redeem_gift_card_message_en"
                              placeholder={t(
                                "Enter Second Dialog Box Body (English)"
                              )}
                              className="form-control"
                            />
                            <TextError name="redeem_gift_card_message_en" />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="redeem_gift_card_message_ar">
                              {t("Second Dialog Box Message Body (عربي)")}
                            </label>
                            <Field
                              type="text"
                              style={{ direction: "rtl" }}
                              id="redeem_gift_card_message_ar"
                              name="redeem_gift_card_message_ar"
                              placeholder={t(
                                "Enter Second Dialog Box Body (عربي)"
                              )}
                              className="form-control"
                            />
                            <TextError name="redeem_gift_card_message_ar" />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="redeem_now_button_en">
                              {t("Redeem Now Button (English)")}
                            </label>
                            <Field
                              type="text"
                              id="redeem_now_button_en"
                              name="redeem_now_button_en"
                              placeholder={t(
                                "Enter Redeem Now Button Text (English)"
                              )}
                              className="form-control"
                            />
                            <TextError name="redeem_now_button_en" />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="redeem_now_button_ar">
                              {t("Redeem Now Button (عربي)")}
                            </label>
                            <Field
                              type="text"
                              style={{ direction: "rtl" }}
                              id="redeem_now_button_ar"
                              name="redeem_now_button_ar"
                              placeholder={t(
                                "Enter Redeem Now Button Text (عربي)"
                              )}
                              className="form-control"
                            />
                            <TextError name="redeem_now_button_ar" />
                          </div>
                        </div>
                        {/* Gift Card Received Notification Section */}
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="gift_card_received_appnotify_title_en">
                              {t("App Notification Title (English)")}
                            </label>
                            <Field
                              type="text"
                              id="gift_card_received_appnotify_title_en"
                              name="gift_card_received_appnotify_title_en"
                              placeholder={t(
                                "Enter App Notification Title (English)"
                              )}
                              className="form-control"
                            />
                            <TextError name="gift_card_received_appnotify_title_en" />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="gift_card_received_appnotify_title_ar">
                              {t("App Notification Title (عربي)")}
                            </label>
                            <Field
                              type="text"
                              style={{ direction: "rtl" }}
                              id="gift_card_received_appnotify_title_ar"
                              name="gift_card_received_appnotify_title_ar"
                              placeholder={t(
                                "Enter App Notification Title (عربي)"
                              )}
                              className="form-control"
                            />
                            <TextError name="gift_card_received_appnotify_title_ar" />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="gift_card_received_appnotify_desc_en">
                              {t("App Notification Description (English)")}
                            </label>
                            <Field
                              as="textarea"
                              rows={3}
                              id="gift_card_received_appnotify_desc_en"
                              name="gift_card_received_appnotify_desc_en"
                              placeholder={t(
                                "Enter App Notification Description (English)"
                              )}
                              className="form-control"
                            />
                            <TextError name="gift_card_received_appnotify_desc_en" />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="gift_card_received_appnotify_desc_ar">
                              {t("App Notification Description (عربي)")}
                            </label>
                            <Field
                              as="textarea"
                              rows={3}
                              style={{ direction: "rtl" }}
                              id="gift_card_received_appnotify_desc_ar"
                              name="gift_card_received_appnotify_desc_ar"
                              placeholder={t(
                                "Enter App Notification Description (عربي)"
                              )}
                              className="form-control"
                            />
                            <TextError name="gift_card_received_appnotify_desc_ar" />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="add_to_wallet_app_notify_title_en">
                              {t(
                                "Add to Wallet App Notification Title (English)"
                              )}
                            </label>
                            <Field
                              type="text"
                              id="add_to_wallet_app_notify_title_en"
                              name="add_to_wallet_app_notify_title_en"
                              placeholder={t(
                                "Enter Add to Wallet App Notification Title (English)"
                              )}
                              className="form-control"
                            />
                            <TextError name="add_to_wallet_app_notify_title_en" />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="add_to_wallet_app_notify_title_ar">
                              {t("Add to Wallet App Notification Title (عربي)")}
                            </label>
                            <Field
                              type="text"
                              style={{ direction: "rtl" }}
                              id="add_to_wallet_app_notify_title_ar"
                              name="add_to_wallet_app_notify_title_ar"
                              placeholder={t(
                                "Enter Add to Wallet App Notification Title (عربي)"
                              )}
                              className="form-control"
                            />
                            <TextError name="add_to_wallet_app_notify_title_ar" />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="add_to_wallet_app_notify_msg_en">
                              {t(
                                "Add to Wallet App Notification Message (English)"
                              )}
                            </label>
                            <Field
                              as="textarea"
                              rows={3}
                              id="add_to_wallet_app_notify_msg_en"
                              name="add_to_wallet_app_notify_msg_en"
                              placeholder={t(
                                "Enter Add to Wallet App Notification Message (English)"
                              )}
                              className="form-control"
                            />
                            <TextError name="add_to_wallet_app_notify_msg_en" />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="add_to_wallet_app_notify_msg_ar">
                              {t(
                                "Add to Wallet App Notification Message (عربي)"
                              )}
                            </label>
                            <Field
                              as="textarea"
                              rows={3}
                              style={{ direction: "rtl" }}
                              id="add_to_wallet_app_notify_msg_ar"
                              name="add_to_wallet_app_notify_msg_ar"
                              placeholder={t(
                                "Enter Add to Wallet App Notification Message (عربي)"
                              )}
                              className="form-control"
                            />
                            <TextError name="add_to_wallet_app_notify_msg_ar" />
                          </div>
                        </div>

                        {/* SMS and Social Media Messages Section */}
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="sms_message_for_receiver_en">
                              {t("SMS Message (English)")}
                            </label>
                            <Field
                              as="textarea"
                              rows={6}
                              id="sms_message_for_receiver_en"
                              name="sms_message_for_receiver_en"
                              placeholder={t("Enter SMS Message (English)")}
                              className="form-control"
                            />
                            <TextError name="sms_message_for_receiver_en" />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="sms_message_for_receiver_ar">
                              {t("SMS Message (عربي)")}
                            </label>
                            <Field
                              as="textarea"
                              rows={6}
                              style={{ direction: "rtl" }}
                              id="sms_message_for_receiver_ar"
                              name="sms_message_for_receiver_ar"
                              placeholder={t("Enter SMS Message (عربي)")}
                              className="form-control"
                            />
                            <TextError name="sms_message_for_receiver_ar" />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="socialmedia_message_for_receiver_en">
                              {t("Social Media Message (English)")}
                            </label>
                            <Field
                              as="textarea"
                              rows={6}
                              id="socialmedia_message_for_receiver_en"
                              name="socialmedia_message_for_receiver_en"
                              placeholder={t(
                                "Enter Social Media Message (English)"
                              )}
                              className="form-control"
                            />
                            <TextError name="socialmedia_message_for_receiver_en" />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="socialmedia_message_for_receiver_ar">
                              {t("Social Media Message (عربي)")}
                            </label>
                            <Field
                              as="textarea"
                              rows={6}
                              style={{ direction: "rtl" }}
                              id="socialmedia_message_for_receiver_ar"
                              name="socialmedia_message_for_receiver_ar"
                              placeholder={t(
                                "Enter Social Media Message (عربي)"
                              )}
                              className="form-control"
                            />
                            <TextError name="socialmedia_message_for_receiver_ar" />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="whatsapp_message_template">
                              {t("Whatsapp Message Template Name")}
                            </label>
                            <Field
                              type="text"
                              id="whatsapp_message_template"
                              name="whatsapp_message_template"
                              placeholder={t(
                                "Enter Whatsapp Message Template Name"
                              )}
                              className="form-control"
                            />
                            <TextError name="whatsapp_message_template" />
                          </div>
                        </div>

                        <div className="col-12 d-flex justify-content-end my-4">
                          <Button
                            type="button"
                            className="mx-2"
                            color="secondary"
                            onClick={() => navigate(-1)}
                          >
                            {t("Cancel")}
                          </Button>

                          <Button
                            type="submit"
                            color="success"
                            disabled={isLoading}
                            className="w_fit_content"
                          >
                            {mode === "add" ? t("Add") : t("Save")}
                          </Button>
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default AddTemplate
